import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useI18n } from "@/hooks/useI18n";
import { HardDrive, Loader2, RefreshCw, XCircle } from "lucide-react";
import {
  useDatabaseSizeStats,
  type IndexSizeRow,
  type StorageBucketRow,
  type TableBloatRow,
  type TableSizeRow,
} from "@/lib/admin/hooks/useDatabaseSizeStats";
import { formatBytes } from "@/lib/admin/utils/format-bytes";

const TOP_ROWS = 20;

// ─── Sotto-blocchi ─────────────────────────────────────────────────────────────

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-gray-900 mt-0.5">{value}</div>
    </div>
  );
}

function SubTable({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div>
        <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </div>
      <div className="max-h-80 overflow-auto rounded-lg border border-gray-100">{children}</div>
    </div>
  );
}

function qualifiedName(schema: string, table: string): string {
  return schema && schema !== "public" ? `${schema}.${table}` : table;
}

// ─── Sezione ───────────────────────────────────────────────────────────────────

/**
 * Peso di database e storage. I dati NON vengono caricati al mount: le RPC
 * interrogano i cataloghi Postgres e sono costose, quindi partono solo al click
 * sul bottone e restano in memoria finché l'utente non ricarica.
 */
export function DatabaseSizeSection() {
  const { t } = useI18n();
  const { data, loading, error, load } = useDatabaseSizeStats();

  const tables: TableSizeRow[] = data?.tables ?? [];
  const indexes: IndexSizeRow[] = data?.indexes ?? [];
  const bloat: TableBloatRow[] = data?.bloat ?? [];
  const storage: StorageBucketRow[] = data?.storage ?? [];

  const topTables = [...tables].sort((a, b) => b.total_bytes - a.total_bytes).slice(0, TOP_ROWS);
  const topIndexes = [...indexes].sort((a, b) => b.size_bytes - a.size_bytes).slice(0, TOP_ROWS);
  const topBloat = [...bloat]
    .filter((r) => r.dead_rows > 0)
    .sort((a, b) => b.dead_pct - a.dead_pct)
    .slice(0, TOP_ROWS);
  const storageTotal = storage.reduce((sum, b) => sum + b.total_bytes, 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-3 pb-3">
        <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-amber-50 text-amber-600 flex-shrink-0">
          <HardDrive className="h-5 w-5" />
        </div>
        <div className="flex flex-col flex-1 min-w-0">
          <CardTitle className="text-base font-semibold">
            {t("adminDashboard.dbSize.title")}
          </CardTitle>
          <span className="text-xs text-gray-500 mt-0.5">
            {t("adminDashboard.dbSize.subtitle")}
          </span>
        </div>
        <button
          onClick={() => load()}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors flex-shrink-0"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {data
            ? t("adminDashboard.dbSize.reloadButton")
            : t("adminDashboard.dbSize.loadButton")}
        </button>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Stato vuoto — prima del click */}
        {!loading && !error && !data && (
          <p className="text-sm text-gray-500 py-8 text-center">
            {t("adminDashboard.dbSize.emptyState")}
          </p>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center gap-2 py-10 text-gray-500 justify-center">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">{t("adminDashboard.dbSize.loading")}</span>
          </div>
        )}

        {/* Errore */}
        {!loading && error && (
          <div className="flex items-center gap-2 py-10 text-red-500 justify-center">
            <XCircle className="h-5 w-5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Riepilogo */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <SummaryTile
                label={t("adminDashboard.dbSize.summary.database")}
                value={formatBytes(data.overview?.database_bytes ?? null)}
              />
              <SummaryTile
                label={t("adminDashboard.dbSize.summary.tables")}
                value={formatBytes(data.overview?.total_table_bytes ?? null)}
              />
              <SummaryTile
                label={t("adminDashboard.dbSize.summary.indexes")}
                value={formatBytes(data.overview?.total_index_bytes ?? null)}
              />
              <SummaryTile
                label={t("adminDashboard.dbSize.summary.toast")}
                value={formatBytes(data.overview?.total_toast_bytes ?? null)}
              />
              <SummaryTile
                label={t("adminDashboard.dbSize.summary.storage")}
                value={formatBytes(storageTotal)}
              />
            </div>

            {/* Top tabelle */}
            <SubTable
              title={t("adminDashboard.dbSize.tables.title")}
              hint={t("adminDashboard.dbSize.tables.hint", { count: TOP_ROWS })}
            >
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="py-2 px-3 text-left font-medium">
                      {t("adminDashboard.dbSize.tables.name")}
                    </th>
                    <th className="py-2 px-3 text-right font-medium">
                      {t("adminDashboard.dbSize.tables.total")}
                    </th>
                    <th className="py-2 px-3 text-right font-medium">
                      {t("adminDashboard.dbSize.tables.data")}
                    </th>
                    <th className="py-2 px-3 text-right font-medium">
                      {t("adminDashboard.dbSize.tables.indexes")}
                    </th>
                    <th className="py-2 px-3 text-right font-medium">
                      {t("adminDashboard.dbSize.tables.toast")}
                    </th>
                    <th className="py-2 px-3 text-right font-medium">
                      {t("adminDashboard.dbSize.tables.rows")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {topTables.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-6 px-3 text-center text-gray-500">
                        {t("adminDashboard.dbSize.noData")}
                      </td>
                    </tr>
                  )}
                  {topTables.map((row) => (
                    <tr key={`${row.schema_name}.${row.table_name}`} className="hover:bg-gray-50">
                      <td className="py-2 px-3 font-mono text-gray-800">
                        {qualifiedName(row.schema_name, row.table_name)}
                      </td>
                      <td className="py-2 px-3 text-right font-semibold text-gray-900">
                        {formatBytes(row.total_bytes)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-600">
                        {formatBytes(row.table_bytes)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-600">
                        {formatBytes(row.index_bytes)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-600">
                        {formatBytes(row.toast_bytes)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-600">
                        {row.row_estimate.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SubTable>

            {/* Top indici */}
            <SubTable
              title={t("adminDashboard.dbSize.indexes.title")}
              hint={t("adminDashboard.dbSize.indexes.hint")}
            >
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="py-2 px-3 text-left font-medium">
                      {t("adminDashboard.dbSize.indexes.name")}
                    </th>
                    <th className="py-2 px-3 text-left font-medium">
                      {t("adminDashboard.dbSize.indexes.table")}
                    </th>
                    <th className="py-2 px-3 text-right font-medium">
                      {t("adminDashboard.dbSize.indexes.size")}
                    </th>
                    <th className="py-2 px-3 text-right font-medium">
                      {t("adminDashboard.dbSize.indexes.scans")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {topIndexes.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-6 px-3 text-center text-gray-500">
                        {t("adminDashboard.dbSize.noData")}
                      </td>
                    </tr>
                  )}
                  {topIndexes.map((row) => {
                    const unused = row.index_scans === 0;
                    return (
                      <tr
                        key={`${row.schema_name}.${row.index_name}`}
                        className={unused ? "bg-red-50/60 hover:bg-red-50" : "hover:bg-gray-50"}
                      >
                        <td className="py-2 px-3 font-mono text-gray-800">{row.index_name}</td>
                        <td className="py-2 px-3 font-mono text-gray-600">
                          {qualifiedName(row.schema_name, row.table_name)}
                        </td>
                        <td className="py-2 px-3 text-right font-semibold text-gray-900">
                          {formatBytes(row.size_bytes)}
                        </td>
                        <td
                          className={`py-2 px-3 text-right ${
                            unused ? "font-semibold text-red-600" : "text-gray-600"
                          }`}
                        >
                          {unused
                            ? t("adminDashboard.dbSize.indexes.unused")
                            : row.index_scans.toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </SubTable>

            {/* Bloat */}
            <SubTable
              title={t("adminDashboard.dbSize.bloat.title")}
              hint={t("adminDashboard.dbSize.bloat.hint")}
            >
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="py-2 px-3 text-left font-medium">
                      {t("adminDashboard.dbSize.bloat.table")}
                    </th>
                    <th className="py-2 px-3 text-right font-medium">
                      {t("adminDashboard.dbSize.bloat.liveRows")}
                    </th>
                    <th className="py-2 px-3 text-right font-medium">
                      {t("adminDashboard.dbSize.bloat.deadRows")}
                    </th>
                    <th className="py-2 px-3 text-right font-medium">
                      {t("adminDashboard.dbSize.bloat.deadPct")}
                    </th>
                    <th className="py-2 px-3 text-right font-medium">
                      {t("adminDashboard.dbSize.bloat.lastVacuum")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {topBloat.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-6 px-3 text-center text-gray-500">
                        {t("adminDashboard.dbSize.bloat.clean")}
                      </td>
                    </tr>
                  )}
                  {topBloat.map((row) => {
                    const lastVacuum = row.last_autovacuum ?? row.last_vacuum;
                    const warn = row.dead_pct >= 20;
                    return (
                      <tr key={`${row.schema_name}.${row.table_name}`} className="hover:bg-gray-50">
                        <td className="py-2 px-3 font-mono text-gray-800">
                          {qualifiedName(row.schema_name, row.table_name)}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-600">
                          {row.live_rows.toLocaleString()}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-600">
                          {row.dead_rows.toLocaleString()}
                        </td>
                        <td
                          className={`py-2 px-3 text-right font-semibold ${
                            warn ? "text-red-600" : "text-gray-900"
                          }`}
                        >
                          {row.dead_pct.toFixed(1)}%
                        </td>
                        <td className="py-2 px-3 text-right text-gray-600">
                          {lastVacuum ? new Date(lastVacuum).toLocaleString() : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </SubTable>

            {/* Storage buckets */}
            <SubTable title={t("adminDashboard.dbSize.storage.title")}>
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="py-2 px-3 text-left font-medium">
                      {t("adminDashboard.dbSize.storage.bucket")}
                    </th>
                    <th className="py-2 px-3 text-right font-medium">
                      {t("adminDashboard.dbSize.storage.objects")}
                    </th>
                    <th className="py-2 px-3 text-right font-medium">
                      {t("adminDashboard.dbSize.storage.size")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {storage.length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-6 px-3 text-center text-gray-500">
                        {t("adminDashboard.dbSize.noData")}
                      </td>
                    </tr>
                  )}
                  {storage.map((row) => (
                    <tr key={row.bucket_id} className="hover:bg-gray-50">
                      <td className="py-2 px-3 font-mono text-gray-800">{row.bucket_id}</td>
                      <td className="py-2 px-3 text-right text-gray-600">
                        {row.object_count.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-right font-semibold text-gray-900">
                        {formatBytes(row.total_bytes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SubTable>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default DatabaseSizeSection;
